// Speed-aware streaming priority (perf). Walking, a symmetric disc around the
// player is the right thing to load. Flying, half that disc is *behind* the
// aircraft and gets evicted seconds later unseen, while the leading edge — the
// terrain about to fill the screen — starves. This module shifts the prefetch
// focus ahead along the velocity vector and scales the per-frame job budget with
// speed, so a fast flight fills what it's flying toward first.
//
// PURE (no DOM/three): the streamer passes a heading in blocks/tick and gets back
// a focus point + budget; unit-tested directly.
import { STREAM, WORLD } from '../../sim/config';

/** Player/aircraft velocity in blocks per sim tick (x/z ground plane). */
export interface Heading { vx: number; vz: number; }

export function speedBlocksPerTick(h: Heading): number { return Math.hypot(h.vx, h.vz); }

/** The prefetch focus in (fractional) chunk coords: the player's chunk shifted
 *  ahead along the velocity vector by ~PREFETCH_LEAD_TICKS of travel, capped at a
 *  fraction of the render radius. Below MOVING_SPEED the focus sits on the player
 *  (symmetric, unchanged walking behaviour). */
export function focusPoint(pcx: number, pcz: number, vx: number, vz: number, radius: number): { fx: number; fz: number } {
  const speed = Math.hypot(vx, vz);
  if (speed < STREAM.MOVING_SPEED) return { fx: pcx, fz: pcz };
  const leadChunks = Math.min(
    (speed / WORLD.SECTION) * STREAM.PREFETCH_LEAD_TICKS,
    radius * STREAM.PREFETCH_LEAD_FRAC,
  );
  return { fx: pcx + (vx / speed) * leadChunks, fz: pcz + (vz / speed) * leadChunks };
}

/** Per-frame budget that grows with speed so a fast flight streams more, clamped
 *  at `max` so one frame never does an unbounded pile of work. Used for both mesh
 *  dispatch (workers) and geometry upload (main thread) — a fast flight needs both
 *  to scale or the un-scaled one starves the disc's leading edge. */
export function scaledBudget(base: number, speedBlocksPerTick: number, max: number = STREAM.MAX_REQUEST_PER_FRAME): number {
  const extra = Math.ceil(Math.max(0, speedBlocksPerTick) * STREAM.SPEED_REQUEST_GAIN);
  return Math.min(max, base + extra);
}

/** Squared distance from a candidate chunk to the focus (lower = higher priority). */
export function focusDist2(cx: number, cz: number, fx: number, fz: number): number {
  const dx = cx - fx, dz = cz - fz;
  return dx * dx + dz * dz;
}

/** The mesh LOD depth to use next frame (0 = full detail; >0 = surface-only shell).
 *  Only engages while flying an aircraft high AND fast; hysteresis on both speed
 *  and altitude-above-terrain keeps it from flip-flopping at the thresholds.
 *  `current` is last frame's LOD, `speed` blocks/tick, `agl` blocks above terrain. */
export function nextViewLod(current: number, flying: boolean, speed: number, agl: number): number {
  if (!flying) return 0; // never LOD on foot
  if (current === 0) {
    return speed >= STREAM.LOD_SPEED && agl >= STREAM.LOD_ALTITUDE ? STREAM.LOD_FLOOR_DEPTH : 0;
  }
  // Engaged: fall back to full detail only when clearly slow or low (the band).
  if (speed < STREAM.LOD_SPEED - STREAM.LOD_SPEED_HYST || agl < STREAM.LOD_ALTITUDE - STREAM.LOD_ALTITUDE_HYST) return 0;
  return current;
}
